import React, { useState } from 'react';
import { AlertTriangleIcon, BellIcon, CalendarCheckIcon, CheckIcon, LinkIcon, Loader2Icon, RefreshCwIcon, UnlinkIcon } from 'lucide-react';
import { EncabezadoPanel, Panel } from '../ui/Basicos';
import { ChipEstadoGoogle, IconoGoogle } from '../ui/Google';
import { useDatos } from '../../contexts/DatosContext';
import { fechaHora, OPCIONES_ANTICIPACION } from '../../utils/google';
import type { Abogado, ConexionGoogle } from '../../types';
type PasoOauth = 'cerrado' | 'cuenta' | 'permisos' | 'conectando';
function correoSugerido(abogado: Abogado): string {
  const base = abogado.correo.split('@')[0].replace(/\./g, '.');
  return `${base}@gmail.com`;
}
function Interruptor({
  etiqueta,
  descripcion,
  activo,
  onCambio,
  deshabilitado
}: {
  etiqueta: string;
  descripcion: string;
  activo: boolean;
  onCambio: (valor: boolean) => void;
  deshabilitado?: boolean;
}) {
  return <label className={`flex items-start justify-between gap-4 py-3 ${deshabilitado ? 'opacity-50' : 'cursor-pointer'}`} data-id="element-735" data-mp="2eed7xlu3h89x">
      <span className="min-w-0" data-id="element-736" data-mp="3sdw8m1miywbu">
        <span className="block text-sm font-medium text-gris-800" data-id="element-737" data-mp="3nhxb9oj7y3za">{etiqueta}</span>
        <span className="mt-0.5 block text-xs text-gris-500" data-id="element-738" data-mp="60e62bme47o5">{descripcion}</span>
      </span>
      <button type="button" role="switch" aria-checked={activo} aria-label={etiqueta} disabled={deshabilitado} onClick={() => onCambio(!activo)} className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors duration-150 ${activo ? 'bg-marino-700' : 'bg-gris-300'}`} data-id="element-739" data-mp="1a7hgbrvon5w">
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform duration-150 ease-out ${activo ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-740" data-mp="oyqo6mva4rvv" />
      </button>
    </label>;
}
export function PanelGoogleCalendar({
  abogado,
  puedeEditar
}: {
  abogado: Abogado;
  puedeEditar: boolean;
}) {
  const {
    conexionGoogleDe,
    conectarGoogle,
    desconectarGoogle,
    actualizarConexionGoogle,
    sincronizarGoogle,
    eventosGoogle
  } = useDatos();
  const conexion = conexionGoogleDe(abogado.id);
  const [paso, setPaso] = useState<PasoOauth>('cerrado');
  const [correo, setCorreo] = useState(correoSugerido(abogado));
  const [aviso, setAviso] = useState('');
  const eventos = eventosGoogle.filter(e => e.abogadoId === abogado.id);
  const conectado = conexion.estado === 'conectado';
  const cambiar = <C extends keyof ConexionGoogle,>(campo: C, valor: ConexionGoogle[C]) => actualizarConexionGoogle({
    ...conexion,
    [campo]: valor
  });
  const autorizar = () => {
    setPaso('conectando');
    window.setTimeout(() => {
      conectarGoogle(abogado.id, correo.trim());
      setPaso('cerrado');
      setAviso(`Cuenta ${correo.trim()} vinculada. Los eventos ya se envían a Google Calendar.`);
    }, 900);
  };
  const claseSelect = 'h-9 w-full rounded border border-gris-200 bg-white px-2.5 text-sm text-gris-700 focus:border-marino-500';
  return <Panel data-id="element-741" data-mp="21hxx35rvq33j">
      <EncabezadoPanel titulo="Google Calendar" descripcion="Vinculación individual: cada persona autoriza su propia cuenta de Google y recibe ahí sus notificaciones." accion={<ChipEstadoGoogle estado={conexion.estado} data-id="element-743" data-mp="1shj9u0vcudxe" />} data-id="element-742" data-mp="21hepoizuux3r" />

      {aviso && <p role="status" className="mx-5 mt-4 rounded border border-exito-100 bg-exito-50 px-3 py-2 text-xs font-medium text-exito-700" data-id="element-744" data-mp="1sjm5p1j6v1a1">
          {aviso}
        </p>}

      {conexion.estado === 'error' && <p className="mx-5 mt-4 flex items-start gap-2 rounded border border-peligro-100 bg-peligro-50 px-3 py-2 text-xs text-peligro-700" data-id="element-745" data-mp="2fgnu6lep82rw">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-746" data-mp="2be18l28mfe35" />
          <span data-id="element-747" data-mp="37zbazomnwvn6">
            {conexion.mensajeError ?? 'La conexión con Google dejó de funcionar.'}
            {conexion.correoGoogle && <span className="mt-0.5 block text-2xs" data-id="element-748" data-mp="2npjxuartdgaf">Cuenta afectada: {conexion.correoGoogle}</span>}
          </span>
        </p>}

      <div className="px-5 py-4" data-id="element-749" data-mp="2spy1lgmdu42n">
        {paso === 'cerrado' ? conectado ? <div className="flex flex-wrap items-center justify-between gap-4 rounded border border-gris-200 bg-gris-50 px-4 py-3" data-id="element-750" data-mp="2eq6kv84he9ky">
              <div className="flex items-center gap-3" data-id="element-751" data-mp="2is0eiwg9edbm">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white ring-1 ring-gris-200" data-id="element-752" data-mp="36h2vs36ze45l">
                  <IconoGoogle className="h-4 w-4" data-id="element-753" data-mp="3be312bw0b2vn" />
                </span>
                <div data-id="element-754" data-mp="2xmoig660h3p4">
                  <p className="text-sm font-semibold text-marino-900" data-id="element-755" data-mp="2xn7s6zlicls2">{conexion.correoGoogle}</p>
                  <p className="tabular mt-0.5 text-2xs text-gris-500" data-id="element-756" data-mp="31n8ibhhyyley">
                    Última sincronización: {fechaHora(conexion.ultimaSync)} ·{' '}
                    {eventos.length} eventos en Google
                  </p>
                </div>
              </div>
              {puedeEditar && <div className="flex items-center gap-2" data-id="element-757" data-mp="1ioaj8p4tr9le">
                  <button type="button" onClick={() => {
            sincronizarGoogle(abogado.id);
            setAviso('Sincronización manual ejecutada.');
          }} className="inline-flex h-9 items-center gap-1.5 rounded border border-gris-200 bg-white px-3 text-xs font-semibold text-marino-700 transition-colors duration-150 hover:border-marino-300" data-id="element-758" data-mp="1dq91q8n1ric1">
                    <RefreshCwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-759" data-mp="19pfd1d1s5zi3" />
                    Sincronizar ahora
                  </button>
                  <button type="button" onClick={() => {
            desconectarGoogle(abogado.id);
            setAviso('Cuenta desvinculada. Ya no se enviarán eventos a Google Calendar.');
          }} className="inline-flex h-9 items-center gap-1.5 rounded border border-gris-200 bg-white px-3 text-xs font-semibold text-gris-600 transition-colors duration-150 hover:border-peligro-200 hover:text-peligro-600" data-id="element-760" data-mp="14pt1r95iitbx">
                    <UnlinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-761" data-mp="2x7wyrbhbjb4a" />
                    Desconectar
                  </button>
                </div>}
            </div> : <div className="flex flex-wrap items-center justify-between gap-4 rounded border border-gris-200 px-4 py-3" data-id="element-762" data-mp="24gd8j7mb0wss">
              <p className="max-w-xl text-xs text-gris-600" data-id="element-763" data-mp="2egnmwvk2sa9j">
                Sin conectar. Al vincular la cuenta, las audiencias, citas y términos de sus
                expedientes aparecerán en su Google Calendar con los recordatorios de Google.
                {conexion.invitacionEnviada && <span className="tabular mt-0.5 block text-2xs text-gris-400" data-id="element-764" data-mp="1qr2yhx55kksv">
                    Invitación enviada el {fechaHora(conexion.invitacionEnviada)}
                  </span>}
              </p>
              {puedeEditar && <button type="button" onClick={() => setPaso('cuenta')} className="inline-flex h-9 items-center gap-2 rounded border border-gris-300 bg-white px-3.5 text-sm font-semibold text-gris-700 transition-colors duration-150 hover:border-marino-300 hover:bg-marino-50" data-id="element-765" data-mp="3uv2hl7zsz1v">
                  <IconoGoogle data-id="element-766" data-mp="3gc0bqeghvbvc" />
                  {conexion.estado === 'error' ? 'Volver a autorizar' : 'Conectar con Google'}
                </button>}
            </div> : <div className="mx-auto max-w-md rounded-lg border border-gris-200 shadow-tarjeta" data-id="element-767" data-mp="37blovanvtopq">
            <div className="flex items-center gap-2 border-b border-gris-200 px-4 py-3" data-id="element-768" data-mp="32cijtq7wjihr">
              <IconoGoogle className="h-5 w-5" data-id="element-769" data-mp="2xd5xr7ouufg8" />
              <p className="text-sm font-semibold text-gris-700" data-id="element-770" data-mp="2xdfq31xdgyp3">
                {paso === 'cuenta' ? 'Elige una cuenta' : 'Acceso a tu cuenta de Google'}
              </p>
            </div>

            {paso === 'cuenta' && <div className="px-4 py-4" data-id="element-771" data-mp="u0ybxe6zxusa">
                <p className="text-xs text-gris-500" data-id="element-772" data-mp="g4b0y7i7l43d">
                  Para continuar, Google compartirá tu nombre, correo y calendarios con{' '}
                  <span className="font-semibold text-gris-700" data-id="element-773" data-mp="l4p9rrrmwi2g">LIMA AMARO</span>.
                </p>
                <label className="mt-3 block" data-id="element-774" data-mp="6bltdnps4adb">
                  <span className="block text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-775" data-mp="b4om5tmqffsg">
                    Cuenta de Google
                  </span>
                  <input autoFocus value={correo} onChange={e => setCorreo(e.target.value)} className="mt-1.5 h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 focus:border-marino-500" placeholder="nombre@gmail.com" data-id="element-776" data-mp="2c4lyoqsl35i" />
                </label>
                <div className="mt-4 flex justify-end gap-2" data-id="element-777" data-mp="34zm5j9qe6c5b">
                  <button type="button" onClick={() => setPaso('cerrado')} className="h-9 rounded px-3 text-sm font-medium text-gris-500 transition-colors duration-150 hover:text-gris-700" data-id="element-778" data-mp="30wzgthp0p7cc">
                    Cancelar
                  </button>
                  <button type="button" disabled={!/^\S+@\S+\.\S+$/.test(correo)} onClick={() => setPaso('permisos')} className="h-9 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700 disabled:opacity-40" data-id="element-779" data-mp="3nukosmptt5ac">
                    Siguiente
                  </button>
                </div>
              </div>}

            {paso === 'permisos' && <div className="px-4 py-4" data-id="element-780" data-mp="6e2qd22zd9b4">
                <p className="text-xs text-gris-600" data-id="element-781" data-mp="3vz60o8yl2vgz">
                  <span className="font-semibold" data-id="element-782" data-mp="3w0qyq8k3wwjl">{correo}</span> otorgará a LIMA AMARO permiso
                  para:
                </p>
                <ul className="mt-3 space-y-2" data-id="element-783" data-mp="3m8bgda4we5y6">
                  {['Ver la lista de calendarios de tu cuenta', 'Crear, actualizar y eliminar eventos en el calendario elegido', 'Configurar los recordatorios de esos eventos'].map(p => <li key={p} className="flex items-start gap-2 text-xs text-gris-600" data-id="element-784" data-mp="dukwvza4znvc">
                      <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-exito-600" data-id="element-785" data-mp="9qn0vxg3yt6i" />
                      {p}
                    </li>)}
                </ul>
                <p className="mt-3 text-2xs text-gris-400" data-id="element-786" data-mp="2uighalr8n1wb">
                  Podrás revocar el acceso en cualquier momento desde este panel o desde tu cuenta
                  de Google.
                </p>
                <div className="mt-4 flex justify-end gap-2" data-id="element-787" data-mp="151kwcpauek42">
                  <button type="button" onClick={() => setPaso('cuenta')} className="h-9 rounded px-3 text-sm font-medium text-gris-500 transition-colors duration-150 hover:text-gris-700" data-id="element-788" data-mp="19zw3utdo0ij4">
                    Atrás
                  </button>
                  <button type="button" onClick={autorizar} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-789" data-mp="1nrssgng4kaar">
                    <LinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-790" data-mp="21rkcb4tbokwo" />
                    Permitir
                  </button>
                </div>
              </div>}

            {paso === 'conectando' && <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gris-600" data-id="element-791" data-mp="7e2qbm8kfw3r">
                <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-792" data-mp="1aoq8hoy1fapl" />
                Autorizando con Google…
              </p>}
          </div>}
      </div>

      {conectado && <div className="border-t border-gris-200 px-5 py-4" data-id="element-793" data-mp="1jq6hptd496s8">
          <h3 className="text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-794" data-mp="1fmi8754xc14c">
            Qué se envía a su Google Calendar
          </h3>
          <div className="mt-1 divide-y divide-gris-100" data-id="element-795" data-mp="1tkydqsr1vlkw">
            <Interruptor etiqueta="Audiencias" descripcion="Audiencias de los expedientes donde aparece como responsable del evento." activo={conexion.sincronizarAudiencias} onCambio={v => cambiar('sincronizarAudiencias', v)} deshabilitado={!puedeEditar} data-id="element-796" data-mp="1oohqe0rodb8v" />
            <Interruptor etiqueta="Citas con clientes" descripcion="Reuniones y citas registradas en la agenda del despacho." activo={conexion.sincronizarCitas} onCambio={v => cambiar('sincronizarCitas', v)} deshabilitado={!puedeEditar} data-id="element-797" data-mp="2cb7t74bul9uj" />
            <Interruptor etiqueta="Vencimientos y términos" descripcion="Términos judiciales y plazos internos a su cargo, como evento de día completo." activo={conexion.sincronizarVencimientos} onCambio={v => cambiar('sincronizarVencimientos', v)} deshabilitado={!puedeEditar} data-id="element-798" data-mp="2hzbajc4jia0o" />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-799" data-mp="2d2b6t7v5xjcq">
            <label data-id="element-800" data-mp="1ycdb86uwlrdi">
              <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-801" data-mp="1ycwrzk8ti9py">
                <CalendarCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-802" data-mp="2382f2mdyhufm" />
                Calendario destino
              </span>
              <select className={`mt-1.5 ${claseSelect}`} value={conexion.calendarioDestino} disabled={!puedeEditar} onChange={e => cambiar('calendarioDestino', e.target.value)} data-id="element-803" data-mp="1ua8jxerxh6u1">
                {conexion.calendarios.map(c => <option key={c.id} value={c.id} data-id="element-804" data-mp="1ci69gi1glwu8">
                    {c.nombre}
                  </option>)}
              </select>
            </label>
            <label data-id="element-805" data-mp="13i9rt0dhly8o">
              <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-806" data-mp="13ita4i71uon8">
                <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-807" data-mp="yiou0ta1zvxj" />
                Anticipación del recordatorio
              </span>
              <select className={`mt-1.5 ${claseSelect}`} value={conexion.anticipacionMinutos} disabled={!puedeEditar} onChange={e => cambiar('anticipacionMinutos', Number(e.target.value))} data-id="element-808" data-mp="tm6qfwqgm3ms">
                {OPCIONES_ANTICIPACION.map(o => <option key={o.minutos} value={o.minutos} data-id="element-809" data-mp="5pjwdmte0pm6">
                    {o.etiqueta}
                  </option>)}
              </select>
            </label>
            <label data-id="element-810" data-mp="fetov3s58aat">
              <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-811" data-mp="febkfjvbuprt">
                <RefreshCwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-812" data-mp="jfdm07cpvdwy" />
                Dirección de sincronización
              </span>
              <select className={`mt-1.5 ${claseSelect}`} value={conexion.direccion} disabled={!puedeEditar} onChange={e => cambiar('direccion', e.target.value as ConexionGoogle['direccion'])} data-id="element-813" data-mp="3ixn19d96qt3e">
                <option value="sistema_a_google" data-id="element-814" data-mp="nkukhdaq0rc">Del sistema hacia Google</option>
                <option value="bidireccional" data-id="element-815" data-mp="1783v88t8jj3j">Bidireccional (Google también al sistema)</option>
              </select>
            </label>
          </div>

          <p className="mt-3 text-2xs text-gris-400" data-id="element-816" data-mp="387h63e1kseqj">
            Al crear, mover o eliminar un evento en el sistema, se crea, actualiza o borra el evento
            correspondiente en «
            {conexion.calendarios.find(c => c.id === conexion.calendarioDestino)?.nombre}» con
            recordatorio configurado.
          </p>
        </div>}

      {conectado && eventos.length > 0 && <div className="border-t border-gris-200" data-id="element-817" data-mp="2pispv5e1exl3">
          <p className="bg-gris-50 px-5 py-2 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-818" data-mp="2pfelxspq6gbj">
            Eventos espejo en Google ({eventos.length})
          </p>
          <ul className="max-h-64 divide-y divide-gris-100 overflow-y-auto scroll-fino" data-id="element-819" data-mp="2z8vfcvly9teo">
            {eventos.slice(0, 12).map(e => <li key={e.id} className="flex items-start gap-3 px-5 py-2.5" data-id="element-820" data-mp="26qzvp7n8f6bs">
                <IconoGoogle className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-821" data-mp="26owwpy43q2ux" />
                <div className="min-w-0 flex-1" data-id="element-822" data-mp="21rnaerc0e7bb">
                  <p className="truncate text-xs font-medium text-gris-800" data-id="element-823" data-mp="hae2p4bfz5rv">{e.titulo}</p>
                  <p className="tabular mt-0.5 text-2xs text-gris-500" data-id="element-824" data-mp="m0uigv2qj3he">
                    {e.fecha}
                    {e.hora ? ` · ${e.hora} h` : ' · todo el día'} · {e.lugar}
                  </p>
                </div>
                <span className="shrink-0 text-2xs uppercase tracking-wide text-gris-400" data-id="element-825" data-mp="ca9ltd6mg914">
                  {e.ultimaAccion}
                </span>
              </li>)}
          </ul>
        </div>}
    </Panel>;
}